/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import {
    getOrderDetailUploads,
    deleteOrderDetailUploadFile,
    downloadOrderDetailUploadFile,
    getPresignedUrlandUploadToS3Directly
} from '../../services/api/orders'
import DefaultLoader from '../Loaders/DefaultLoader'
import { OrderUploadResponse } from '../../model'
import DynamicFaIcon from '../DynamicFaIcon/DynamicFaIcon'
import ActionButton from '../ActionButton/ActionButton'
import Pagination from '../../components/Pagination/Pagination'
import { useParams, useSearchParams } from 'react-router-dom'
import { SearchOptions } from '../OrderOverview/OrderOverview'
import { useUpdateEffect } from 'react-use'
import { openDownloadUrl } from '../../helpers/utils'
interface EnabledSearchParams {
    page?: string | number | undefined
}

const defaultSearchOptions = {
    page: 1,
    pageSize: 10
}

const UploadContainer: React.FC<{}> = () => {
    const { orderId } = useParams()
    const [files, setFiles] = useState<OrderUploadResponse[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [isUploading, setIsUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [showMessage, setShowMessage] = useState(false)
    const [message, setMessage] = useState('')
    const [total, setTotal] = useState<number>(0)

    const [searchParams, setSearchParams] = useSearchParams()
    const [searchOptions, setSearchOptions] = useState<SearchOptions>({
        ...defaultSearchOptions
    })

    const handleFileUpload = async (filesToUpload: File[]) => {
        if (!orderId || !filesToUpload.length) return

        const promises = filesToUpload.map((file) => {
            return getPresignedUrlandUploadToS3Directly(Number(orderId), file)
        })

        try {
            handleCloseMessage()
            setIsUploading(true)
            setUploadProgress(0)

            // Simulate progress (in a real app, you might use axios's onUploadProgress)
            const progressInterval = setInterval(() => {
                setUploadProgress((prev) => {
                    const newProgress = prev + 10
                    if (newProgress >= 90) clearInterval(progressInterval)
                    return newProgress
                })
            }, 100)

            await Promise.all(promises).catch((error) => {
                handleSetMessage('Error: ' + error.response.data.message)
            })

            clearInterval(progressInterval)
            setUploadProgress(100)

            fetchUploads()
        } catch (error: any) {
            console.error('Upload error:', error)
            handleSetMessage('Error: ' + error.response.data.message)
        } finally {
            setTimeout(() => {
                setIsUploading(false)
                setUploadProgress(0)
            }, 500)
        }
    }

    const { getRootProps, getInputProps, isFocused, isDragAccept } = useDropzone({
        onDrop: handleFileUpload
    })

    const handleCloseMessage = () => {
        setShowMessage(false)
        setMessage('')
    }
    const handleSetMessage = (message: string) => {
        setShowMessage(true)
        setMessage(message)
        setTimeout(() => handleCloseMessage(), 3500)
    }

    const fetchUploads = useCallback(async () => {
        try {
            setIsLoading(true)
            const response = await getOrderDetailUploads(Number(orderId))
            setFiles(response.items || []) // [fakeUpload]
            setTotal(response.total || 0)
        } catch (error: any) {
            console.error('Error fetching uploads:', error)
            handleSetMessage('Error: ' + error.response.data.message)
        } finally {
            setIsLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId])

    const handleDelete = async (fileId: string, fileName: string) => {
        try {
            handleCloseMessage()
            await deleteOrderDetailUploadFile(Number(orderId), fileId)
            fetchUploads()
        } catch (error: any) {
            console.error('Delete error:', error)
            handleSetMessage('Error: ' + error.response.data.message)
        }
    }

    const handleDownload = async (fileId: string) => {
        try {
            handleCloseMessage()
            const { downloadUrl } = await downloadOrderDetailUploadFile(Number(orderId), fileId)
            openDownloadUrl(downloadUrl)
        } catch (error: any) {
            console.error('Download error:', error)
            handleSetMessage('Error' + error.response.data.message)
        }
    }

    const getFileIcon = (fileName?: string | null) => {
        const iconClass = 'w-5 h-5 text-gray-500'

        // Guard against null/undefined filenames
        if (!fileName) return <DynamicFaIcon iconName='file' className={iconClass} />

        const extension = fileName.split('.').pop()?.toLowerCase()

        if (!extension) return <DynamicFaIcon iconName='file' className={iconClass} />

        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp']
        const docExtensions = ['doc', 'docx']
        const xlsExtensions = ['xls', 'xlsx']
        const pdfExtensions = ['pdf']

        if (imageExtensions.includes(extension)) {
            return <DynamicFaIcon iconName='file-image' className={iconClass} />
        } else if (docExtensions.includes(extension)) {
            return <DynamicFaIcon iconName='file-word' className={iconClass} />
        } else if (xlsExtensions.includes(extension)) {
            return <DynamicFaIcon iconName='file-excel' className={iconClass} />
        } else if (pdfExtensions.includes(extension)) {
            return <DynamicFaIcon iconName='file-pdf' className={iconClass} />
        }

        return <DynamicFaIcon iconName='file' className={iconClass} />
    }

    const updateSearchParams = (params: EnabledSearchParams) => {
        const paramsToSet = Object.fromEntries(Object.entries(params).filter(([_, value]) => value !== undefined))
        setSearchParams(paramsToSet)
    }

    const handlePageChange = (page: number) => {
        const params: EnabledSearchParams = { page }
        updateSearchParams(params)
    }

    const updateSearchOptions = () => {
        setSearchOptions({
            ...searchOptions,
            page: searchParams.get('page') ? parseInt(searchParams.get('page') || '1') : 1
        })
    }

    useEffect(() => {
        // Initialize based on search params or just load the first results
        if (searchParams.has('page')) {
            updateSearchOptions()
        } else {
            fetchUploads()
        }
    }, [])

    useUpdateEffect(() => {
        fetchUploads()
    }, [searchOptions])

    useUpdateEffect(() => {
        updateSearchOptions()
    }, [searchParams])

    if (isLoading) {
        return (
            <div className='flex justify-center items-center h-64'>
                <DefaultLoader />
            </div>
        )
    }

    return (
        <>
            <div className='py-4 min-w-14'>
                {isUploading ? ( //isUploading
                    <div className='mb-4 px-6'>
                        <div className='mb-1 text-sm font-medium text-gray-700'>
                            Bestand uploaden... {uploadProgress}%
                        </div>
                        <div className='w-full bg-gray-200 rounded-full h-2.5'>
                            <div
                                className='bg-[--tb-primary] h-2.5 rounded-full transition-all duration-300'
                                style={{ width: `${uploadProgress}%` }}
                            ></div>
                        </div>
                    </div>
                ) : showMessage ? (
                    <div
                        className='mx-6 bg-[--tb-blushPrimary] border border-dashed rounded-lg p-4 text-center text-red-500 cursor-pointer transition-colors border-gray-300 hover:border-[--tb-primary]'
                        onClick={handleCloseMessage}
                    >
                        <p className='text-sm font-medium'>{message}</p>
                    </div>
                ) : (
                    <div
                        {...getRootProps({
                            className: `mx-6 bg-[--tb-blushPrimary] border border-dashed rounded-lg text-center cursor-pointer transition-colors border-gray-300 hover:border-[--tb-primary]`,
                            style: { borderColor: isDragAccept || isFocused ? `var(--tb-primary)` : `` }
                        })}
                    >
                        <input {...getInputProps()} />
                        <div className='flex flex-row space-x-2 items-center justify-center text-[--tb-primary] p-4'>
                            <DynamicFaIcon iconName='paperclip-vertical' />
                            <p className='text-sm font-medium'>
                                Sleep en zet hier schetsen, bestaande beelden, plattegronden, of klik om te selecteren
                            </p>
                        </div>
                    </div>
                )}

                <div className='mt-4 justify-between flex flex-col'>
                    {!isLoading && total === 0 ? (
                        <div className='text-center py-8 text-gray-500'>Nog geen bestanden geüpload</div>
                    ) : (
                        <ul>
                            {files.map((file) => (
                                <li className='w-full border-t last:border-b' key={file.id}>
                                    <div className='px-4 py-4 grid grid-cols-3 sm:grid-cols-6 items-center justify-between sm:px-6'>
                                        <div className='grid grid-cols-1 sm:grid-cols-5 items-center gap-x-2 col-span-2 sm:col-span-5'>
                                            <div className='flex items-center px-2 gap-x-1 col-span-3'>
                                                <div className='flex-shrink-0'>{getFileIcon(file.fileName)}</div>
                                                <p className='text-sm font-medium truncate'>{file.fileName}</p>
                                            </div>
                                            <div className='col-span-2 px-2'>
                                                <div className='flex items-center space-x-2'>
                                                    <DynamicFaIcon iconName='upload' className='text-[--tb-green]' />
                                                    <p className='text-sm text-gray-900'>
                                                        Geüpload bestand:{' '}
                                                        {new Date(file.created || '').toLocaleDateString('nl-NL')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='flex-shrink-0 col-span-1 flex justify-end'>
                                            <ActionButton
                                                options={[
                                                    {
                                                        text: 'Downloaden',
                                                        type: 'default',
                                                        action: () => handleDownload(file.id.toString()),
                                                        contextType: 'bestand',
                                                        showConfirmModal: false
                                                    },
                                                    {
                                                        text: 'Verwijderen',
                                                        type: 'danger',
                                                        action: () => handleDelete(file.id.toString(), file.fileName),
                                                        contextType: 'bestand'
                                                    }
                                                ]}
                                            />
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
            {total > 10 && (
                <div className='px-2'>
                    <Pagination
                        page={searchOptions.page}
                        itemsPerPage={searchOptions.pageSize}
                        totalItems={total}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}
        </>
    )
}

export default UploadContainer
